import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import { api } from "@/lib/api";

// Exact-size HTML print for 62 x 100mm Brother QL labels.
// Renders the label(s) into an off-screen root that becomes the ONLY visible
// content during printing, then triggers the OS print sheet directly. On iOS
// (AirPrint) this is a single tap — no PDF download / Share / More dance.
const PRINT_CSS = `
  @page { size: 62mm 100mm; margin: 0; }
  #vs-print-root { position: fixed; left: -100000px; top: 0; }
  #vs-print-root .vs-p-label { width: 62mm; height: 100mm; padding: 3mm 3.5mm; box-sizing: border-box; background:#fff; color:#0f172a; font-family:'IBM Plex Sans','Helvetica Neue',Arial,sans-serif; display:flex; flex-direction:column; gap:2mm; overflow:hidden; }
  #vs-print-root .vs-p-brand { font-size:7pt; letter-spacing:0.15em; text-transform:uppercase; color:#64748b; }
  #vs-print-root .vs-p-name { font-size:14pt; font-weight:700; line-height:1.05; margin-top:1mm; word-break:break-word; }
  #vs-print-root .vs-p-length { font-size:14pt; font-weight:700; line-height:1.05; margin-top:0.5mm; color:#2563eb; }
  #vs-print-root .vs-p-incoming { display:inline-block; margin-top:1mm; padding:0.5mm 1.5mm; background:#fef3c7; color:#92400e; border:0.2mm solid #fcd34d; font-size:6pt; letter-spacing:0.15em; text-transform:uppercase; border-radius:0.5mm; }
  #vs-print-root .vs-p-sku { font-family:'JetBrains Mono',monospace; font-size:8pt; word-break:break-all; line-height:1.2; padding:1.5mm 2mm; background:#f1f5f9; border-radius:0.8mm; }
  #vs-print-root .vs-p-grid { display:grid; grid-template-columns:1fr 1fr; gap:1.2mm 2.5mm; }
  #vs-print-root .vs-p-flabel { color:#64748b; text-transform:uppercase; letter-spacing:0.08em; font-size:6pt; }
  #vs-print-root .vs-p-fvalue { font-family:'JetBrains Mono',monospace; font-size:8pt; font-weight:600; }
  #vs-print-root .vs-p-warn { color:#b45309 !important; }
  #vs-print-root .vs-p-bad { color:#b91c1c !important; }
  #vs-print-root .vs-p-code { display:flex; justify-content:center; align-items:center; flex:1; min-height:20mm; padding:1mm 0; }
  #vs-print-root .vs-p-code img.qr { width:44mm; height:44mm; }
  #vs-print-root .vs-p-code img.bc { width:100%; max-width:52mm; height:16mm; object-fit:contain; }
  @media print {
    html, body { margin:0 !important; padding:0 !important; background:#fff !important; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
    body > *:not(#vs-print-root) { display:none !important; }
    #vs-print-root { position:static !important; left:0 !important; }
    #vs-print-root .vs-p-page { page-break-after: always; break-after: page; }
    #vs-print-root .vs-p-page:last-child { page-break-after: auto; break-after: auto; }
    #vs-print-root .vs-p-label { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  }
`;

function barcodeDataUrl(text) {
  const canvas = document.createElement("canvas");
  JsBarcode(canvas, text, { format: "CODE128", displayValue: false, margin: 0, width: 2, height: 60 });
  return canvas.toDataURL("image/png");
}

function esc(s) {
  return String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

function labelHtml(label, codeType, qrDataUrl) {
  const { roll, product, manufacturer, series } = label;
  const isIncoming = roll.status === "incoming";
  const len = Number(isIncoming ? roll.initial_length_m : roll.current_length_m || 0);
  const exp = roll.expiry_date ? new Date(roll.expiry_date) : null;
  const days = exp ? Math.ceil((exp - new Date()) / 86400000) : null;
  const expired = days != null && days < 0;
  const expiring = days != null && days >= 0 && days <= 90;
  const brand = [manufacturer?.name, series?.name].filter(Boolean).join("  ·  ");
  const expCls = expired ? "vs-p-bad" : expiring ? "vs-p-warn" : "";
  const codeHtml = codeType === "barcode"
    ? `<img class="bc" src="${barcodeDataUrl(roll.sku || "")}" alt="" />`
    : `<img class="qr" src="${qrDataUrl || ""}" alt="" />`;
  return `<div class="vs-p-page"><div class="vs-p-label">
    <div>
      <div class="vs-p-brand">${esc(brand || "—")}</div>
      <div class="vs-p-name">${esc(product.name || "")}</div>
      <div class="vs-p-length">${len.toFixed(2)} m</div>
      ${isIncoming ? `<div class="vs-p-incoming">Incoming</div>` : ""}
    </div>
    <div class="vs-p-sku">${esc(roll.sku || "")}</div>
    <div class="vs-p-grid">
      <div><div class="vs-p-flabel">Width</div><div class="vs-p-fvalue">${Number(roll.width_m || 0).toFixed(2)}m</div></div>
      <div><div class="vs-p-flabel">Batch</div><div class="vs-p-fvalue">${esc(roll.batch_number || "—")}</div></div>
      <div><div class="vs-p-flabel">Expires</div><div class="vs-p-fvalue ${expCls}">${esc(roll.expiry_date || "—")}</div></div>
      <div><div class="vs-p-flabel">Location</div><div class="vs-p-fvalue">${esc(roll.location || "—")}</div></div>
    </div>
    <div class="vs-p-code">${codeHtml}</div>
  </div></div>`;
}

function ensureStyle() {
  if (!document.getElementById("vs-print-style")) {
    const s = document.createElement("style");
    s.id = "vs-print-style";
    s.textContent = PRINT_CSS;
    document.head.appendChild(s);
  }
}

function printLabelHtmlStrings(htmlStrings) {
  if (!htmlStrings.length) return;
  ensureStyle();
  let root = document.getElementById("vs-print-root");
  if (root) root.remove();
  root = document.createElement("div");
  root.id = "vs-print-root";
  root.innerHTML = htmlStrings.join("");
  document.body.appendChild(root);

  const cleanup = () => {
    const r = document.getElementById("vs-print-root");
    if (r) r.remove();
    window.removeEventListener("afterprint", cleanup);
  };
  window.addEventListener("afterprint", cleanup);

  const imgs = Array.from(root.querySelectorAll("img"));
  const ready = Promise.all(imgs.map((img) => (img.complete ? Promise.resolve() : new Promise((res) => { img.onload = img.onerror = res; }))));
  ready.then(() => {
    setTimeout(() => {
      try { window.print(); } catch (e) { /* no-op */ }
      setTimeout(cleanup, 60000); // fallback if afterprint never fires (some mobile browsers)
    }, 120);
  });
}

// Print already-resolved labels + pre-rendered QR data URLs (used by the dialog).
export function printLabelsHtml(labels, codeType = "qr", qrImages = {}) {
  const html = labels.map((l) => labelHtml(l, codeType, qrImages[l.roll.id])).filter(Boolean);
  printLabelHtmlStrings(html);
}

// Fetch roll data by ids, then print (manual print + auto-print on roll creation).
export async function fetchAndPrintLabels(rollIds, codeType = "qr") {
  const origin = window.location.origin;
  const rolls = (await Promise.all(
    rollIds.map((id) => api.get(`/rolls/${id}`).then((r) => r.data).catch(() => null))
  )).filter(Boolean);
  if (!rolls.length) return;

  const pids = [...new Set(rolls.map((r) => r.product_id))];
  const prods = (await Promise.all(
    pids.map((pid) => api.get(`/products/${pid}`).then((r) => r.data).catch(() => null))
  )).filter(Boolean);
  const productMap = Object.fromEntries(prods.map((p) => [p.id, p]));

  const [{ data: ms }, { data: ss }] = await Promise.all([api.get("/manufacturers"), api.get("/series")]);
  const mfrMap = Object.fromEntries(ms.map((m) => [m.id, m]));
  const serMap = Object.fromEntries(ss.map((s) => [s.id, s]));

  const labels = rolls.map((roll) => {
    const product = productMap[roll.product_id];
    if (!product) return null;
    return {
      roll,
      product,
      manufacturer: mfrMap[product.manufacturer_id],
      series: serMap[product.series_id],
      scanTarget: `${origin}/rolls/${roll.id}?action=cut`,
    };
  }).filter(Boolean);

  const qrImages = Object.fromEntries(await Promise.all(
    labels.map(async (l) => [
      l.roll.id,
      await QRCode.toDataURL(l.scanTarget || l.roll.sku, { margin: 0, width: 512, errorCorrectionLevel: "M" }),
    ])
  ));

  printLabelsHtml(labels, codeType, qrImages);
}
